import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import { Link } from 'react-router-dom';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ── Stripe checkout form (must live inside <Elements>) ──────────────────────
const CheckoutForm = ({ invoiceId, amount }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setSubmitting(true);
        setError('');

        const { error: stripeError } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/payment?success=true&invoice=${invoiceId}`,
            },
        });

        // If we reach here, confirmPayment threw a validation/card error
        if (stripeError) {
            setError(stripeError.message);
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="checkout-form">
            <PaymentElement
                options={{
                    layout: 'tabs',
                    defaultValues: { billingDetails: {} },
                }}
            />
            {error && (
                <div className="payment-error-msg">
                    <span>⚠️</span> {error}
                </div>
            )}
            <button
                type="submit"
                className="btn btn-primary btn-lg payment-submit-btn"
                disabled={!stripe || submitting}
            >
                {submitting ? (
                    <>
                        <span className="payment-spinner" /> Processing...
                    </>
                ) : (
                    `Pay $${parseFloat(amount).toFixed(2)}`
                )}
            </button>
            <p className="payment-secure-note">
                <span>🔒</span> Encrypted &amp; secured by Stripe. We never store card details.
            </p>
        </form>
    );
};

// ── Main Payment page ───────────────────────────────────────────────────────
const Payment = () => {
    const [step, setStep] = useState('details');
    const [invoiceId, setInvoiceId] = useState('');
    const [amount, setAmount] = useState('');
    const [email, setEmail] = useState('');
    const [description, setDescription] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState('');
    const [success, setSuccess] = useState(false);

    // Pre-fill from URL params (e.g. /payment?invoice=INV-001&amount=2500)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('invoice')) setInvoiceId(params.get('invoice'));
        if (params.get('amount'))  setAmount(params.get('amount'));
        if (params.get('success') === 'true') setSuccess(true);
    }, []);

    const handleProceed = async (e) => {
        e.preventDefault();
        setLoading(true);
        setFetchError('');

        try {
            const res = await fetch(`${API_URL}/create-payment-intent`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    amount: parseFloat(amount),
                    invoiceId: invoiceId.trim(),
                    invoiceDescription: description.trim(),
                    customerEmail: email.trim() || undefined,
                }),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Server error.');

            setClientSecret(data.clientSecret);
            setStep('checkout');
        } catch (err) {
            setFetchError(err.message || 'Could not connect to payment server. Is it running?');
        } finally {
            setLoading(false);
        }
    };

    const stripeAppearance = {
        theme: 'stripe',
        variables: {
            colorPrimary: '#0284c7',
            colorBackground: '#ffffff',
            colorText: '#0f172a',
            colorDanger: '#ef4444',
            fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
            spacingUnit: '4px',
            borderRadius: '8px',
            fontSizeBase: '15px',
        },
        rules: {
            '.Input': { border: '1px solid rgba(15,23,42,0.18)', boxShadow: 'none' },
            '.Input:focus': { border: '1px solid #0284c7', boxShadow: '0 0 0 3px rgba(2,132,199,0.12)' },
            '.Tab': { border: '1px solid rgba(15,23,42,0.1)' },
            '.Tab--selected': { borderColor: '#0284c7', backgroundColor: 'rgba(2,132,199,0.06)' },
        },
    };

    // ── Success screen ──
    if (success) {
        return (
            <div className="page payment-page">
                <div className="payment-container">
                    <div className="payment-success-card">
                        <div className="payment-success-icon">✅</div>
                        <h1>Payment Received</h1>
                        <p>
                            Thank you! Your payment for invoice <strong>{invoiceId || 'your invoice'}</strong> has been
                            submitted. You'll receive a receipt via email once it's confirmed.
                        </p>
                        <p className="payment-success-note">
                            Bank transfers (ACH) typically settle in 3–5 business days.
                            Card payments are usually instant.
                        </p>
                        <Link to="/" className="btn btn-primary">Back to Home</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page payment-page">
            <div className="payment-container">

                {/* Brand header */}
                <div className="payment-brand">
                    <Link to="/" className="payment-brand-link">
                        <span className="payment-brand-name">OnOneGroup</span>
                    </Link>
                    <span className="payment-secure-badge">🔒 Secure Payment</span>
                </div>

                {/* Progress indicator */}
                <div className="payment-steps">
                    <div className={`payment-step ${step === 'details' ? 'active' : step === 'checkout' ? 'done' : ''}`}>
                        <span>1</span> Invoice Details
                    </div>
                    <div className="payment-step-divider" />
                    <div className={`payment-step ${step === 'checkout' ? 'active' : ''}`}>
                        <span>2</span> Payment
                    </div>
                </div>

                {/* ── Step 1: Invoice details ── */}
                {step === 'details' && (
                    <div className="payment-card">
                        <div className="payment-card-header">
                            <h2>Invoice Details</h2>
                            <p>Enter your invoice number and amount to pay.</p>
                        </div>

                        <form onSubmit={handleProceed} className="payment-details-form">
                            <div className="form-row-2">
                                <div className="form-group">
                                    <label className="form-label">Invoice Number <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="e.g. INV-2026-001"
                                        value={invoiceId}
                                        onChange={e => setInvoiceId(e.target.value)}
                                        required
                                        autoFocus
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Amount (USD) <span className="required">*</span></label>
                                    <div className="payment-amount-wrap">
                                        <span className="payment-currency-symbol">$</span>
                                        <input
                                            type="number"
                                            className="form-input payment-amount-input"
                                            placeholder="0.00"
                                            min="1"
                                            step="0.01"
                                            value={amount}
                                            onChange={e => setAmount(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Your Email (optional — for receipt)</label>
                                <input
                                    type="email"
                                    className="form-input"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Invoice Description (optional)</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="e.g. Web Development — May 2026"
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                />
                            </div>

                            <div className="payment-methods-preview">
                                <p className="pmp-label">Accepted payment methods:</p>
                                <div className="pmp-badges">
                                    <span className="pmp-badge">💳 Credit / Debit Card</span>
                                    <span className="pmp-badge">🏦 Bank Transfer (ACH)</span>
                                </div>
                            </div>

                            {fetchError && (
                                <div className="payment-error-msg">
                                    <span>⚠️</span> {fetchError}
                                </div>
                            )}

                            <button
                                type="submit"
                                className="btn btn-primary btn-lg payment-submit-btn"
                                disabled={loading}
                            >
                                {loading ? (
                                    <><span className="payment-spinner" /> Initializing...</>
                                ) : (
                                    'Continue to Payment →'
                                )}
                            </button>
                        </form>
                    </div>
                )}

                {/* ── Step 2: Stripe checkout ── */}
                {step === 'checkout' && clientSecret && (
                    <div className="payment-card">
                        <button
                            className="payment-back-btn"
                            onClick={() => setStep('details')}
                        >
                            ← Back
                        </button>

                        <div className="payment-summary-box">
                            <div className="psb-row">
                                <span className="psb-label">Invoice</span>
                                <span className="psb-value">{invoiceId}</span>
                            </div>
                            {description && (
                                <div className="psb-row">
                                    <span className="psb-label">Description</span>
                                    <span className="psb-value psb-desc">{description}</span>
                                </div>
                            )}
                            <div className="psb-row psb-total">
                                <span className="psb-label">Amount Due</span>
                                <span className="psb-amount">${parseFloat(amount).toFixed(2)}</span>
                            </div>
                        </div>

                        <div className="payment-card-header">
                            <h2>Choose Payment Method</h2>
                            <p>Select card or bank transfer below.</p>
                        </div>

                        <Elements
                            stripe={stripePromise}
                            options={{ clientSecret, appearance: stripeAppearance }}
                        >
                            <CheckoutForm invoiceId={invoiceId} amount={amount} />
                        </Elements>
                    </div>
                )}

                <div className="payment-footer-note">
                    Payments processed by <strong>Stripe</strong>. Questions?{' '}
                    <Link to="/contact">Contact us</Link>.
                </div>

            </div>
        </div>
    );
};

export default Payment;
